import type { Money } from '@wodi/domain';
import { moneyFromCents } from '@wodi/domain';
import { z } from 'zod';

// Money on the wire (docs/adr/0006-money-and-rounding.md; finance plan decision 19).
//
// A bigint does not fit a JSON number, so an amount crosses the API as integer cents in a
// string with its currency: `{ "currency": "KES", "cents": "120050" }` is KES 1,200.50.
// What a person types is parsed on the client with `parseMoney` from @wodi/domain; the
// server only ever accepts cents, never a decimal or a float.

export const CURRENCIES = ['KES'] as const;

/** Whole cents, no sign, no leading zeros, no decimals, exponents or spaces. */
export const centsStringSchema = z
  .string()
  .regex(/^(0|[1-9]\d{0,17})$/, 'Must be a whole number of cents, written as digits');

/** Whole cents that may be negative, for credits and refunds. */
export const signedCentsStringSchema = z
  .string()
  .regex(/^(0|-?[1-9]\d{0,17})$/, 'Must be a whole number of cents, written as digits');

/** An amount that can't be negative: a price, a co-pay, a limit. */
export const amountSchema = z.object({
  currency: z.enum(CURRENCIES),
  cents: centsStringSchema,
});

export const signedAmountSchema = z.object({
  currency: z.enum(CURRENCIES),
  cents: signedCentsStringSchema,
});

export type AmountView = z.infer<typeof amountSchema>;

export function amountToMoney(amount: z.infer<typeof signedAmountSchema>): Money {
  return moneyFromCents(BigInt(amount.cents), amount.currency);
}

export function moneyToAmount(money: Money): AmountView {
  return { currency: money.currency, cents: money.cents.toString() };
}
